import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Kunne ikke bekrefte betaling. Sjekk e-post eller kontakt support.');
      return;
    }

    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(
        `${API}/stripe/checkout/${sessionId}/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(response.data);
        toast.success('✅ Betaling vellykket!');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Betalingen utløp.');
        return;
      }

      // Continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  const tierNames = {
    minimum: 'Minimum',
    starter: 'Starter',
    basic: 'Basic',
    professional: 'Professional',
    enterprise: 'Enterprise'
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Bekrefter betaling...</h2>
            <p className="text-gray-600">Vennligst vent mens vi behandler betalingen din.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Betaling vellykket!</h2>
            <p className="text-gray-600 mb-6">
              Ditt {tierNames[paymentDetails?.tier] || 'nye'} abonnement er nå aktivert.
            </p>
            
            {paymentDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-2">Detaljer:</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500">Abonnement:</span> {tierNames[paymentDetails.tier]}</p>
                  <p><span className="text-gray-500">Fakturering:</span> {paymentDetails.billing_period === 'yearly' ? 'Årlig' : 'Månedlig'}</p>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <Link
                to="/dashboard"
                className="block w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
              >
                Gå til Dashboard
              </Link>
              <Link
                to="/subscription"
                className="block w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
              >
                Se abonnementsdetaljer
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Noe gikk galt</h2>
            <p className="text-gray-600 mb-6">
              Vi kunne ikke bekrefte betalingen din. Vennligst sjekk e-post eller kontakt support.
            </p>
            
            <div className="space-y-3">
              <Link
                to="/subscription"
                className="block w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
              >
                Prøv igjen
              </Link>
              <a
                href="mailto:support@systemklar.no"
                className="block w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
              >
                Kontakt support
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SubscriptionSuccess;
